/**
 * Statut d'affichage Admin — promotions attribuées à un client (PromotionClient).
 * Ne modifie pas l'éligibilité checkout ; lecture seule pour l'UI.
 */

const BADGE = {
  active: 'bg-green-100 text-green-800',
  used: 'bg-amber-100 text-amber-900',
  expired: 'bg-gray-100 text-gray-700',
  inactive: 'bg-red-100 text-red-800',
  scheduled: 'bg-blue-100 text-blue-800',
};

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function isPonctuelle(promotion) {
  return (promotion?.typeRecurrence || 'PONCTUELLE') === 'PONCTUELLE';
}

function getUtilisations(promotion) {
  return Array.isArray(promotion?.utilisations) ? promotion.utilisations : [];
}

/** @returns {{ key: string, label: string, className: string }} */
export function getClientPromotionDisplayStatus(promotion, now = new Date()) {
  const utilisations = getUtilisations(promotion);

  if (isPonctuelle(promotion) && utilisations.length > 0) {
    return { key: 'used', label: 'Utilisé', className: BADGE.used };
  }

  if (promotion?.actif === false) {
    return { key: 'inactive', label: 'Inactif', className: BADGE.inactive };
  }

  const debut = promotion?.dateDebut ? new Date(promotion.dateDebut) : null;
  const fin = promotion?.dateFin ? endOfDay(promotion.dateFin) : null;

  if (debut && now < debut) {
    return { key: 'scheduled', label: 'À venir', className: BADGE.scheduled };
  }

  if (fin && now > fin) {
    return { key: 'expired', label: 'Expiré', className: BADGE.expired };
  }

  return { key: 'active', label: 'Actif', className: BADGE.active };
}

function formatUsageDateTime(dateUtilisation) {
  if (!dateUtilisation) return null;
  const d = new Date(dateUtilisation);
  if (Number.isNaN(d.getTime())) return null;
  const datePart = d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart} à ${timePart}`;
}

function pickLatestUtilisation(utilisations) {
  if (!utilisations.length) return null;
  return [...utilisations].sort(
    (a, b) => new Date(b.dateUtilisation).getTime() - new Date(a.dateUtilisation).getTime()
  )[0];
}

/**
 * Détail compact si promo ponctuelle consommée (source : utilisations_promotions_clients).
 * @returns {{ dateTimeLabel: string, originLabel: string, referenceLabel: string | null } | null}
 */
export function getClientPromotionUsageDetail(promotion) {
  if (!isPonctuelle(promotion)) return null;
  const utilisations = getUtilisations(promotion);
  if (!utilisations.length) return null;

  const latest = pickLatestUtilisation(utilisations);
  if (!latest) return null;

  const dateTimeLabel = formatUsageDateTime(latest.dateUtilisation);
  if (!dateTimeLabel) return null;

  let originLabel = 'RDV';
  let referenceLabel = null;

  if (latest.commandeId) {
    originLabel = 'Commande';
    const numero = latest.commande?.numero;
    referenceLabel = numero ? String(numero) : latest.commandeId.slice(0, 8);
  }

  return { dateTimeLabel, originLabel, referenceLabel };
}
